import { useEffect, useState } from "react";
import axios from "axios";
import { BsStar, BsStarFill, BsStarHalf } from "react-icons/bs";

function StarDisplay({ rating, size = "sm" }) {
  const sizeClass = size === "lg" ? "text-xl" : "text-sm";
  return (
    <div className={`flex gap-0.5 ${sizeClass} text-amber-400`}>
      {[1, 2, 3, 4, 5].map((i) => {
        if (rating >= i) return <BsStarFill key={i} />;
        if (rating >= i - 0.5) return <BsStarHalf key={i} />;
        return <BsStar key={i} className="text-gray-300" />;
      })}
    </div>
  );
}

function RatingBar({ label, count, total }) {
  const pct = total > 0 ? Math.round((count / total) * 100) : 0;
  return (
    <div className="flex items-center gap-2 text-sm">
      <span className="w-4 text-gray-500 text-right shrink-0">{label}</span>
      <BsStarFill className="text-amber-400 text-xs shrink-0" />
      <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
        <div
          className="h-full bg-amber-400 rounded-full transition-all duration-700"
          style={{ width: `${pct}%` }}
        />
      </div>
      <span className="w-5 text-gray-400 text-xs text-right shrink-0">{count}</span>
    </div>
  );
}

function ReviewCard({ review }) {
  const { userId, rating, comment, createdAt, reservationId } = review;

  const name = userId
    ? `${userId.name} ${userId.surname.charAt(0)}.`
    : "Voyageur anonyme";

  const initials = userId
    ? `${userId.name.charAt(0)}${userId.surname.charAt(0)}`
    : "?";

  const date = new Date(createdAt).toLocaleDateString("fr-FR", {
    month: "long",
    year: "numeric",
  });

  const nights = reservationId?.totalNights;

  return (
    <div className="py-5 border-b border-gray-100 last:border-0">
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-full bg-teal-600 text-white flex items-center justify-center text-xs font-semibold shrink-0 select-none">
          {initials}
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <span className="text-sm font-medium text-gray-800">{name}</span>
            <span className="text-xs text-gray-400">{date}</span>
          </div>

          {nights && (
            <p className="text-xs text-gray-400 mt-0.5">
              Séjour de {nights} nuit{nights > 1 ? "s" : ""}
            </p>
          )}

          <div className="mt-1">
            <StarDisplay rating={rating} />
          </div>

          {comment && (
            <p className="mt-2 text-sm text-gray-600 leading-relaxed">{comment}</p>
          )}
        </div>
      </div>
    </div>
  );
}

export default function HotelReviews({ hotelId, onStatsLoad }) {
  const [data, setData] = useState(null);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!hotelId) return;
    const fetchReviews = async () => {
      try {
        setLoading(true);
        const res = await axios.get(
          `https://api.komorotel.com/api/reviews/${hotelId}`,
          { params: { page } }
        );
        setData(res.data);

        // Remonte le score au parent pour HotelInfo
        if (res.data.avgRating && onStatsLoad) {
          onStatsLoad(res.data.avgRating, res.data.totalReviews);
        }
      } catch (err) {
        setError("Impossible de charger les avis.");
      } finally {
        setLoading(false);
      }
    };
    fetchReviews();
  }, [hotelId, page]);

  if (!loading && data?.totalReviews === 0) return null;

  return (
    <section id="avis" className="px-4 md:px-0 pb-16">
      <h2 className="text-xl font-semibold text-gray-900 mb-6">
        Avis des voyageurs
      </h2>

      {loading && (
        <div className="flex flex-col gap-3">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="h-20 bg-gray-100 rounded-xl animate-pulse" />
          ))}
        </div>
      )}

      {error && <p className="text-sm text-red-500">{error}</p>}

      {!loading && data && (
        <>
          <div className="flex flex-col sm:flex-row gap-8 mb-8 p-5 bg-gray-50 rounded-2xl border border-gray-100">
            <div className="flex flex-col items-center justify-center sm:border-r border-gray-200 sm:pr-8 sm:min-w-[110px]">
              <span className="text-5xl font-bold text-gray-900 leading-none tracking-tight">
                {data.avgRating ?? "–"}
              </span>
              <div className="mt-1.5">
                <StarDisplay rating={data.avgRating} size="lg" />
              </div>
              <span className="text-xs text-gray-400 mt-1.5">
                {data.totalReviews} avis
              </span>
            </div>

            <div className="flex-1 flex flex-col justify-center gap-2.5">
              {[5, 4, 3, 2, 1].map((star) => (
                <RatingBar
                  key={star}
                  label={star}
                  count={data.distribution?.[star] ?? 0}
                  total={data.totalReviews}
                />
              ))}
            </div>
          </div>

          <div>
            {data.reviews.map((review) => (
              <ReviewCard key={review._id} review={review} />
            ))}
          </div>

          {data.totalPages > 1 && (
            <div className="flex items-center justify-center gap-3 mt-8">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="px-4 py-2 text-sm rounded-xl border border-gray-200 text-gray-600 hover:border-gray-400 disabled:opacity-30 disabled:cursor-not-allowed transition"
              >
                ← Précédent
              </button>
              <span className="text-sm text-gray-500">
                {page} / {data.totalPages}
              </span>
              <button
                onClick={() => setPage((p) => Math.min(data.totalPages, p + 1))}
                disabled={page === data.totalPages}
                className="px-4 py-2 text-sm rounded-xl border border-gray-200 text-gray-600 hover:border-gray-400 disabled:opacity-30 disabled:cursor-not-allowed transition"
              >
                Suivant →
              </button>
            </div>
          )}
        </>
      )}
    </section>
  );
}